import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../api';
import Select from '../components/Select';
import Field from '../components/ui/Field';
import Metrica, { MetricaGrid } from '../components/ui/Metrica';
import DataTable from '../components/ui/DataTable';
import Estado from '../components/ui/Estado';
import EmptyState from '../components/ui/EmptyState';
import PageHeader from '../components/ui/PageHeader';
import Icon from '../components/ui/Icon';
import { fmt, fmtBRL } from '../utils/format';

/** Gera as opcoes de ano: ano atual + 2 anteriores. */
function opcoesAno() {
  const atual = new Date().getFullYear();
  return [atual, atual - 1, atual - 2].map(a => ({ value: String(a), label: String(a) }));
}

/** Gera CSV no navegador e forca download. */
function baixarCsv(lancamentos, ano) {
  const bom = '\uFEFF'; // BOM UTF-8 para Excel
  let csv = bom + 'Data;Tipo;Descrição;Quantidade;Unidade;Valor;Fornecedor;Em nome de;Safra;Saldo acumulado\n';
  for (const l of lancamentos) {
    const num = (v) => v != null ? v.toFixed(2).replace('.', ',') : '';
    csv += `${l.data};${l.tipo};${escapar(l.descricao)};${num(l.quantidade)};${escapar(l.unidade)};`
      + `${num(l.valor)};${escapar(l.fornecedor)};${escapar(l.emNomeDe)};${escapar(l.safra)};${num(l.saldoAcumulado)}\n`;
  }
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `livro-caixa-${ano}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function escapar(valor) {
  if (valor == null) return '';
  const s = String(valor);
  if (s.includes(';') || s.includes('"') || s.includes('\n')) {
    return '"' + s.replace(/"/g, '""') + '"';
  }
  return s;
}

export default function LivroCaixa() {
  const [ano, setAno] = useState(String(new Date().getFullYear()));
  const [dados, setDados] = useState(null);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState(null);
  const [semPlano, setSemPlano] = useState(false);
  const [msgPlano, setMsgPlano] = useState('');
  // Assinatura carregada em paralelo para exibir o aviso de trial.
  const [assinatura, setAssinatura] = useState(null);

  const carregar = useCallback(() => {
    setCarregando(true);
    setErro(null);
    setSemPlano(false);
    // Busca assinatura em paralelo: precisamos saber se é trial sem livro caixa
    // no plano, para mostrar o aviso mesmo que o backend libere o recurso.
    Promise.all([api.getLivroCaixa(ano), api.getAssinatura()])
      .then(([livro, ass]) => {
        setDados(livro);
        setAssinatura(ass);
      })
      .catch(e => {
        if (e.status === 403) {
          setSemPlano(true);
          setMsgPlano(e.message || 'Seu plano não inclui o Livro Caixa.');
        } else {
          setErro(e.message || 'Erro ao carregar o livro caixa.');
        }
        setDados(null);
        // Tenta buscar a assinatura mesmo com erro no livro: o aviso de trial
        // precisa aparecer independentemente.
        api.getAssinatura().then(setAssinatura).catch(() => {});
      })
      .finally(() => setCarregando(false));
  }, [ano]);

  useEffect(() => { carregar(); }, [carregar]);

  if (semPlano) {
    return (
      <div>
        <PageHeader titulo="Livro caixa" icone="livro-caixa" />
        <EmptyState
          icone="cadeado"
          titulo="Recurso não disponível no seu plano"
          descricao={msgPlano}
          acao={{ to: '/minha-assinatura', label: 'Ver planos disponíveis' }}
        />
      </div>
    );
  }

  // Os papéis controlam o cartão no celular: sem eles cada lançamento virava um
  // bloco de nove linhas de peso igual, e o produtor rolava muito sem achar o que
  // importa. No cartão a descrição é o título e o valor é o destaque.
  //
  // A ordem aqui é a do desktop (data primeiro, como num livro caixa). O cartão
  // não depende dela: título e destaque têm posição explícita no grid, e o resto
  // é auto-posicionado nas linhas seguintes na ordem em que aparece.
  const colunas = [
    { chave: 'data', titulo: 'Data', papel: 'subtitulo', render: l => formatarData(l.data) },
    { chave: 'tipo', titulo: 'Tipo', papel: 'chip', render: l => (
      <span className={`selo ${l.tipo === 'ENTRADA' ? 'selo--ok' : 'selo--perigo'}`}>
        {l.tipo === 'ENTRADA' ? 'Entrada' : 'Saída'}
      </span>
    )},
    { chave: 'descricao', titulo: 'Descrição', papel: 'titulo' },
    // Custo avulso não tem quantidade; vazio faz a linha desaparecer do cartão
    // em vez de mostrar "QTD —".
    { chave: 'quantidade', titulo: 'Qtd', alinhar: 'direita',
      render: l => l.quantidade != null ? `${fmt(l.quantidade)} ${l.unidade || ''}`.trim() : '' },
    { chave: 'valor', titulo: 'Valor', alinhar: 'direita', papel: 'destaque',
      render: l => (
        <span className={l.tipo === 'ENTRADA' ? 'valor-positivo' : 'valor-negativo'}>
          {l.tipo === 'ENTRADA' ? '' : '−'}{fmtBRL(l.valor)}
        </span>
      ) },
    // As duas colunas de contraparte continuam sempre presentes (sem
    // `ocultarSeVazia`): são o que o contador usa para separar o livro por CPF e
    // por origem, e sumir a coluna toda esconderia que o campo existe.
    //
    // Mas devolvem string vazia em vez de "Não informado": no cartão do celular,
    // "Não informado" repetido em toda linha era ruído puro, e o CSS esconde
    // célula vazia por linha. No desktop a coluna segue lá, com a célula em branco.
    { chave: 'fornecedor', titulo: 'Fornecedor', render: l => l.fornecedor || '' },
    { chave: 'emNomeDe', titulo: 'Em nome de', render: l => l.emNomeDe || '' },
    // A safra já aparece na descrição das vendas; no celular é repetição.
    { chave: 'safra', titulo: 'Safra', ocultarSeVazia: true, papel: 'oculto' },
    { chave: 'saldoAcumulado', titulo: 'Saldo', alinhar: 'direita', render: l => (
      <strong className={l.saldoAcumulado >= 0 ? 'valor-positivo' : 'valor-negativo'}>
        {fmtBRL(l.saldoAcumulado)}
      </strong>
    )},
  ];

  const tomSaldo = dados && dados.saldo >= 0 ? 'positivo' : 'negativo';
  // Aviso de trial: o backend libera o recurso durante o trial mesmo que o plano
  // não inclua, mas o produtor precisa saber que isso vai mudar.
  const eTrialSemLivroCaixa = assinatura?.status === 'TRIAL'
    && assinatura?.livroCaixa === false;

  return (
    <div>
      <PageHeader
        titulo="Livro caixa"
        icone="livro-caixa"
        descricao="Entradas e saídas do ano, montadas a partir de vendas, compras e custos."
      >
        <div className="campo-estreito">
          <Field label="Ano">
            <Select value={ano} onChange={setAno} options={opcoesAno()} placeholder="Selecione o ano" />
          </Field>
        </div>
      </PageHeader>

      {/* Aviso de trial ─────────────────────────────────────────────────── */}
      {eTrialSemLivroCaixa && (
        <div className="aviso aviso--atencao">
          <strong>Você está no período de avaliação gratuita.</strong> O Livro Caixa
          está disponível agora para você conhecer o recurso, mas{' '}
          <strong>seu plano atual ({assinatura.plano}) não inclui o Livro Caixa</strong>.
          Após o trial, será necessário um plano que inclua este recurso para continuar
          acessando. <Link to="/minha-assinatura">Ver planos</Link>
        </div>
      )}

      <Estado carregando={carregando} erro={erro} aoTentarNovamente={carregar} skeleton="card" itens={3}>
        {dados && (
          <>
            <MetricaGrid min={140}>
              <Metrica label="Entradas" value={fmtBRL(dados.totalEntradas)} tom="receita"
                dica="Total de vendas no período." />
              <Metrica label="Saídas" value={fmtBRL(dados.totalSaidas)} tom="custo"
                dica="Total de compras e custos no período." />
              <Metrica label="Saldo" value={fmtBRL(dados.saldo)} tom={tomSaldo} destaque
                dica="Entradas menos saídas." />
            </MetricaGrid>

            {/* Aviso ao contador ──────────────────────────────────────────── */}
            <div className="aviso aviso--info">
              <strong>Para uso com seu contador.</strong>{' '}
              Este livro caixa foi gerado a partir dos lançamentos registrados no sistema
              (vendas, compras de insumos e custos). Ele deve ser <strong>revisado e validado
              por um contador</strong> antes de ser usado para declarações fiscais. Os dados
              refletem apenas o que foi cadastrado pelo produtor — lançamentos ausentes ou
              incorretos no sistema não aparecerão aqui.
            </div>

            <div className="card">
              <div className="secao-titulo">
                <h2>Lançamentos</h2>
                <button
                  type="button"
                  className="btn-secondary btn-sm"
                  onClick={() => baixarCsv(dados.lancamentos, ano)}
                >
                  <Icon nome="documento" tamanho={15} />
                  Exportar CSV
                </button>
              </div>
              <DataTable
                colunas={colunas}
                linhas={dados.lancamentos}
                vazio={{ icone: 'livro-caixa', titulo: 'Nenhum lançamento no ano', descricao: 'Registre vendas, compras ou custos para vê-los aqui.' }}
              />
            </div>
          </>
        )}
      </Estado>
    </div>
  );
}

function formatarData(dataStr) {
  if (!dataStr) return '—';
  const [ano, mes, dia] = dataStr.split('-');
  return `${dia}/${mes}/${ano}`;
}
